import { Button, ButtonGroup, cn, Paragraph } from '@quickmd-llc/design-system'
import { homeDeliveryGuideHref } from 'pages/HomeDeliveryGuide/homeDeliveryGuideLink'
import { HOME_DELIVERY_NUDGE_ENTRY_SOURCE } from 'pages/HomeDeliveryGuide/homeDeliveryTracking'
import { useId } from 'react'
import { type HomeDeliveryGuidance } from '../requestedPharmacy'
import { QMD_PHARMACY_NAME } from './prescriptionFulfilment'

type Props = {
  /** `null` while there is nothing to show: no Home Delivery request, or the provider pressed "Got it". */
  tone: HomeDeliveryGuidance | null
  onDismiss: () => void
  /** "How it works" / "How to change", which opens the guide in a new tab. */
  onHowClick: () => void
}

/**
 * The guidance strip along the bottom of the Requested pharmacy card.
 *
 * Built here rather than on DS `Banner` because it sits flush inside the card (no border or radius
 * of its own) and bolds "Order Set", which `Banner`'s string-only description cannot. The tints are
 * Banner's own `informative` / `warning` classes so the two stay visually one family, and the
 * buttons follow Banner's action rule: neutral on informative, `theme='warning'` on warning.
 *
 * The guide opens in a new tab so the visit, and a video call in it, stays where it is. From the
 * warning it opens at "How to change…", the step the provider needs next.
 */
export const HomeDeliveryGuidanceStrip = ({ tone, onDismiss, onHowClick }: Props) => {
  const buttonTheme = tone === 'warning' ? ({ theme: 'warning' } as const) : {}
  const newTabNoteId = useId()

  return (
    <div
      className={cn(
        tone && 'flex flex-col gap-sp-1 border-t border-border-medium p-sp-2',
        tone === 'warning' && 'bg-surface-warning text-text-warning',
        tone === 'info' && 'bg-coastal-blue-10 text-text-highlight',
      )}>
      {/*
        Always mounted, so a warning that arrives over MQTT mid-call is a change inside a live region
        that already exists, which is the only kind screen readers reliably announce. Polite rather
        than `alert`: it should not cut across the call. Only the message is inside it, so the buttons
        are not read out with it.
      */}
      <div role='status'>
        {tone === 'warning' ?
          <Paragraph size='sm' className='text-inherit'>
            Patient requested Home Delivery. This order is for pickup. Prescribe from{' '}
            <strong className='font-semibold'>Order Set</strong> in ScriptSure.
          </Paragraph>
        : tone === 'info' ?
          <Paragraph size='sm' className='text-inherit'>
            Prescribe from <strong className='font-semibold'>Order Set</strong> in ScriptSure. Orders are filled by{' '}
            {QMD_PHARMACY_NAME}.
          </Paragraph>
        : null}
      </div>
      {tone ?
        <ButtonGroup type='horizontal'>
          <Button size='s' priority='secondary' {...buttonTheme} onClick={onDismiss}>
            Got it
          </Button>
          <Button
            size='s'
            priority='primary'
            {...buttonTheme}
            to={homeDeliveryGuideHref({
              section: tone === 'warning' ? 'how-to-change' : undefined,
              from: HOME_DELIVERY_NUDGE_ENTRY_SOURCE[tone],
            })}
            onClick={onHowClick}
            target='_blank'
            rel='noopener noreferrer'
            aria-describedby={newTabNoteId}>
            {tone === 'warning' ? 'How to change' : 'How it works'}
          </Button>
        </ButtonGroup>
      : null}
      {/* A description rather than part of the name: the visible label stays the name. */}
      <span id={newTabNoteId} hidden>
        Opens in a new tab
      </span>
    </div>
  )
}
